import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { ExtensionCategory, ExtensionState } from '../../types'
import { Box, Card, useMediaQuery, useTheme } from '@mui/material'
import { Chip } from 'src/Components'
import ContractState from './ContractState'
import { TextSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface StyleProps {
  borderColor: string
}
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'default',
    alignSelf: 'stretch',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    border: '1px solid',
    borderColor: 'transparent',
    boxShadow: theme.effectStyles.shadowStrong,
    gap: theme.spacing(2),
    '&:hover': {
      borderColor: props.borderColor,
      cursor: 'pointer',
    },
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
      padding: theme.spacing(4, 2),
      width: 200,
      flexDirection: 'column',
    },
  },
  logo: {
    gap: theme.spacing(2),
    height: 24,
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      minHeight: 24,
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '35%',
    },
    [theme.breakpoints.down(SMALL_MOBILE_BREAKPOINT)]: {
      maxWidth: '30%',
    },
  },
}))

export interface SummaryCardProps {
  category: ExtensionCategory
  logo: React.ReactElement
  displayContractStatePlaceholder: boolean
  contractState?: ExtensionState
  isLoading?: boolean
  onClick: () => void
}

const SMALL_MOBILE_BREAKPOINT = 390

/**
 * Displays a summary of an installation extension. Includes the category, logo, and contract state.
 *
 * @story - linked to storybook
 */
const SummaryCard: React.FC<SummaryCardProps> = ({ category, logo, displayContractStatePlaceholder, contractState, isLoading = false, onClick }) => {
  const { classes } = useStyles({ borderColor: category.paletteColor.main })
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const upSmContractStyle = upSm ? { position: 'absolute', top: 2, right: 2 } : {}
  const categoryName = t(`installation-extension.category.${category.name}`)

  return (
    <Card className={classes.container} onClick={onClick} data-qa={`installation-extension-card-${category.name}`}>
      {isLoading ? <TextSkeleton variant="h3" sx={{ minWidth: upSm ? '80%' : '35%', minHeight: 24 }} /> : <Box className={classes.logo}>{logo}</Box>}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, overflow: 'hidden' }}>
        <Chip textKey={categoryName} paletteColor={category.paletteColor} isLoading={isLoading} variant={category.variant} />
        <ContractState
          contractState={contractState}
          displayText={false}
          sx={upSmContractStyle}
          hasEmptyPlaceholder={displayContractStatePlaceholder}
          isLoading={isLoading}
        />
      </Box>
    </Card>
  )
}

export default SummaryCard
